import {Tools} from "./tools.js";
import root_css from './styles.css';
import {submitHandlerFactory} from "./handlers/submit-handler-factory.js";
import {elementBuilder} from "./element-builder.js";
import {ColumnElement} from "./element-types/column-element.js";
import {HiddenElement} from "./element-types/index.js";
import synfucion_css from './sync-dd-nonshadow.css';

/**
 * Represents a customizable form component that operates within a shadow DOM environment.
 * The `TiberForm` class extends `ColumnElement` to inherit mobile-friendly behaviors and manages
 * a variety of configurable form elements, listeners, and styles.
 */
export class TiberForm extends ColumnElement {
    formType;

    configuration;
    hostElement;
    formId;
    formData;
    formContainer;
    elements;

    encapsulation;
    shadowRoot;
    forgeToken;

    resizeObserver;
    subscriptions;

    formVariables = {};
    phpRoot;

    /**
     * Retrieves the grid definition configuration.
     *
     * @return {Object} The grid configuration object.
     */
    get gridDefinition() {
        return this.configuration.grid;
    }

    /**
     * Retrieves the host of the shadow root if the shadowRoot is an instance of ShadowRoot.
     * Otherwise, returns the shadowRoot itself.
     *
     * @return {Element|ShadowRoot} The host element of the shadow root when shadowRoot is an instance of ShadowRoot,
     * or the shadowRoot itself if it is not an instance of ShadowRoot.
     */
    get shadowHost() {
        return this.shadowRoot instanceof ShadowRoot ? this.shadowRoot.host : this.shadowRoot;
    }

    /**
     * Returns the configured mobile boundaries or a default value if not set.
     *
     * @return {number} The mobile boundary value, typically used to determine responsive behaviors. Defaults to 768 if not configured.
     */
    get mobileBounds() {
        return this.configuration?.mobileBounds ?? 768;
    }

    /**
     * Determines if the current viewport width is considered "mobile" based on a predefined mobile boundary.
     * It uses the `shadowWidth` property if available; otherwise, it falls back to `window.innerWidth`.
     *
     * @return {boolean} Returns true if the viewport width is less than the mobile boundary, otherwise false.
     */
    get isMobile() {
        return ((this.shadowWidth ?? 0) > 0 ? this.shadowWidth : window.innerWidth) < this.mobileBounds;
    }

    /**
     * Retrieves the width of the shadow host element.
     *
     * @return {number} The width of the shadow host, determined by `offsetWidth` if available, or fallback to `width`.
     */
    get shadowWidth() {
        const container = this.shadowHost;

        const bounds = container.getBoundingClientRect();
        return bounds.offsetWidth ?? bounds.width;
    }

    /**
     * Checks if the configuration contains valid fields or sections.
     * Evaluates whether the `fields` or `sections` property in `configuration` exists and is an array.
     *
     * @return {boolean} True if `fields` or `sections` are present and are arrays; otherwise, false.
     */
    get hasElements() {
        return (this.configuration?.fields != null && Array.isArray(this.configuration.fields)) ||
            (this.configuration?.sections != null && Array.isArray(this.configuration.sections));
    }

    /**
     * Checks if the current object has no elements.
     *
     * @return {boolean} Returns true if the object has no elements, otherwise false.
     */
    get noElements() {
        return !this.hasElements;
    }
    /**
     * CSTOR
     * @param hostElement HTML element to replace with the form
     * @param configuration Configuration JSON loaded for the form
     */
    constructor(hostElement, configuration) {
        super(() => this.isMobile);

        this.configuration = configuration;
        this.hostElement = hostElement;

        this.formId = `${this.configuration.id}_${Tools.generateShortHash()}`;
        this.formData = {};
        this.setFormVariables(hostElement);
    }

    /**
     * Initializes the form - not called from constructor for browser compatibility and
     * debuggable purposes
     */
    async initialize(phpRoot) {
        if (this.hostElement === null) {
            console.error(`Unable to find element with id ${this.configuration.id}`);
            return;
        }

        this.phpRoot = phpRoot;
        const shadowHost = this.createShadowHost();
        this.shadowRoot = await this.createShadowRoot(shadowHost);

        /** replace the form element tag **/
        this.hostElement.parentNode.replaceChild(shadowHost, this.hostElement);

        this.formContainer = this.createFormWrapper(this.shadowRoot);

        //store shadow root globally so it can be referenced by the Syncfusion dropdown 
        window.tiberFormShadowRoot = this.shadowRoot;
        
        this.attachAntiForgery(this.formContainer);
        this.buildElements(this.formContainer);
        this.submitHandler(this.formContainer);

        this.attachListeners();
    }

    /**
     * Populates the formVariables object with key-value pairs derived from the dataset
     * attributes of the provided hostElement. Dataset attributes starting with "field"
     * are transformed into camelCase keys for the formVariables object.
     *
     * @param {HTMLElement} hostElement - The HTML element containing dataset attributes
     *                                     to extract and transform into formVariables.
     * @return {void} - This method does not return a value.
     */
    setFormVariables(hostElement) {
        Object.keys(hostElement.dataset)
            .filter(key => key.startsWith("field"))
            .forEach(key => {
                const variable = key
                    .replace(/^field/, '')
                    .replace(/^([A-Z])/, (match) => match.toLowerCase());

                this.formVariables[variable] = hostElement.dataset[key];
            });
    }

    /**
     * Applies custom styles to the provided shadow root using a specified CSS link,
     * based on the configuration or host element's dataset.
     *
     * @param {ShadowRoot} shadowRoot - The shadow root where the custom styles will be applied.
     * @param {string} encapsulation - The encapsulation mode used to handle style injection.
     * @return {void} Resolves without returning a value.
     */
    async customStyles(shadowRoot, encapsulation) {
        const cssLink = this.configuration.customStyleSheet ?? this.configuration.customStyles ?? this.hostElement.dataset.customStyles;
        if (cssLink == null) {
            return;
        }

        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = cssLink;

        this.appendStyle(shadowRoot, encapsulation, link);
    }

    /**
     * Attaches an anti-forgery token to a wrapper element for enhanced security.
     *
     * @param {HTMLElement} wrapper - The HTML element to which the anti-forgery token field will be attached.
     * @return {void} - Does not return a value. Executes token attachment and rendering logic.
     */
    attachAntiForgery(wrapper) {
        if ((this.configuration.token ?? "").length <= 0) {
            return;
        }

        this.forgeToken = Object.assign(new HiddenElement(this.formData, wrapper, this), {field: "x-token", value: this.configuration.token});
        this.forgeToken.render();
    }

    /**
     * Attaches necessary event listeners and observers for the component.
     *
     * Initializes a ResizeObserver to monitor changes to the size of the shadow host element
     * and emits resize events. Subscribes to resize events from the elements service and
     * reconfigures the grid for the component if the form wrapper is found.
     *
     * @return {void} This method does not return a value.
     */
    attachListeners() {
        if (this.shadowHost === null) {
            console.warn("Form listeners not established: Host or Shadow Host not initialized or found.");
            return;
        }

        this.shadowHost.addEventListener("click", (e) => {
            e.stopPropagation();
        });

        this.resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                this.elements.emitResizeEvent({
                   host: this.shadowHost,
                   target: entry.target,
                   contentRect: entry.contentRect
                });
            }
        });

        this.resizeObserver.observe(this.shadowHost);

        const subscription = this.elements.subscribeToResize(args => {

            const wrapper = this.shadowRoot.querySelectorAll('.tiber-form-wrapper');
            if (wrapper == null || wrapper.length !== 1) {
                console.error("Unable to configure grid: Form wrapper not found or multiple wrapper found.");
                return;
            }

            this.defineGrid(wrapper[0]);
        });

        this.subscriptions = [...this.subscriptions ?? [], subscription];
    }

    /**
     * Creates and configures a shadow host element for the form.
     *
     * @return {HTMLDivElement} The created and configured shadow host element.
     */
    createShadowHost() {
        const shadowHost = document.createElement("div");

        shadowHost.id = this.formId;
        shadowHost.classList.add(['tiber-form']);

        shadowHost.style.width = this.configuration.width ?? '100%';
        shadowHost.style.position = 'relative';

        return shadowHost;
    }

    /**
     * Creates the shadow environment for building the form
     * @param shadowHost Host element to house the form
     * @returns {*} Shadow host as a DOM element
     */
    async createShadowRoot(shadowHost) {

        this.encapsulation = this.hostElement.dataset.encapsulation ?? "shadow";
        const shadowRoot = this.encapsulation === "shadow" ? shadowHost.attachShadow({mode: 'open'}) : shadowHost;

        this.buildSyncfusionStyle(shadowRoot, this.encapsulation);
        this.buildRootStyles(shadowRoot, this.encapsulation);
        await this.customStyles(shadowRoot, this.encapsulation);

        return shadowRoot;
    }

    /**
     * Creates and returns a wrapper element for a form. The type of element created
     * is determined by the `formType` property. Defaults to "div" if `formType` is not specified.
     * The wrapper is assigned the default class 'tiber-form-wrapper' and optionally additional
     * CSS classes based on the configuration.
     *
     * @return {HTMLElement} The created form wrapper element with the appropriate classes.
     */
    createFormWrapperElement() {
        const formWrapper = document.createElement(this.formType ?? "div");
        formWrapper.classList.add(['tiber-form-wrapper']);

        if (this.configuration.cssClass != null) {
            formWrapper.classList.add(this.configuration.cssClass);
        }

        return formWrapper;
    }

    /**
     * Creates the wrapper for the form
     * @param shadowRoot The Shadow DOM object to attach the wrapper
     * @returns {HTMLDivElement} The DOM element for the form wrapper
     */
    createFormWrapper(shadowRoot) {
        const formWrapper = this.createFormWrapperElement();
        this.defineGrid(formWrapper);

        shadowRoot.appendChild(formWrapper);
        return formWrapper;
    }

    /**
     * Builds the default styles for the shadow area
     * @param shadowRoot The root DOM element for the shadow area
     */
    buildRootStyles(shadowRoot, encapsulation) {
        const style = document.createElement('style');
        style.textContent = root_css;
        this.appendStyle(shadowRoot, encapsulation, style);
    }

    /**
     * Appends a style element to a specified root element, configuring it based on encapsulation settings.
     *
     * @param {ShadowRoot|HTMLElement} shadowRoot - The root element where the style will be appended.
     * @param {boolean} encapsulation - Indicates whether to use encapsulation for the style.
     * @param {HTMLStyleElement} styleElement - The style element to be appended.
     * @param {boolean} topLevel - Determines if the style is applied at the top level.
     * @return {void} - Does not return a value.
     */
    appendStyle(shadowRoot, encapsulation, styleElement, topLevel) {
        const rootElement = this.cssAppendRoot(shadowRoot, encapsulation);
        const newStyle = this.prepCssStyle(styleElement, encapsulation);
        rootElement.appendChild(newStyle);
    }

    /**
     * Appends a root to the document based on the encapsulation type.
     *
     * @param {ShadowRoot|HTMLElement} shadowRoot - The shadow root element to use when encapsulation is shadow.
     * @param {string} encapsulation - The type of encapsulation to determine the root; valid value is "shadow".
     * @return {ShadowRoot|HTMLElement} - The selected root element; either the shadow root or the document head.
     */
    cssAppendRoot(shadowRoot, encapsulation) {
        return encapsulation === "shadow" ? shadowRoot : document.head;
    }

    /**
     * Builds and appends the necessary Syncfusion style elements to the specified container.
     *
     * @param {ShadowRoot} shadowRoot - The shadow root or container element where the styles will be appended.
     * @param {string} encapsulation - Encapsulation type specifying where styles should be applied
     * (e.g., "shadow" for shadow DOM or other types for global use).
     * @return {void} - Does not return any value.
     */
    buildSyncfusionStyle(shadowRoot, encapsulation) {
        const style = document.createElement('link');
        style.setAttribute('rel', 'stylesheet');
        style.setAttribute('href', 'https://cdn.syncfusion.com/ej2/28.2.3/material.css');

        this.appendStyle(shadowRoot, encapsulation, style);

        const tiberDdStyle = document.createElement('style');
        tiberDdStyle.textContent = synfucion_css;
        this.appendStyle(shadowRoot, encapsulation, tiberDdStyle);

        if (encapsulation !== "shadow") {
            return;
        }

        document.head.appendChild(style.cloneNode(true));
        document.head.appendChild(tiberDdStyle.cloneNode(true));

    }

    /**
     * Preps the CSS worksheet based on if the host is a shadow dom or not
     * @param styleElement Element to be prepped
     * @param encapsulation The encapsulation mode for the form.
     * @returns {*} if the styleElement is a STYLE tag, returns the modified element. If not returns the element unmodified.
     */
    prepCssStyle(styleElement, encapsulation) {
        if (styleElement.tagName !== "STYLE") {
            return styleElement;
        }

        styleElement.type = 'text/css';
        styleElement.dataset.module = "tiber-form";

        const encapsulationMap = {
            shadow: ["root", "host"]
        }

        const [search, replacement] = encapsulationMap[encapsulation] ?? ["host", "root"];

        const regex = new RegExp(`:${search}`, 'g');

        if (search && replacement) {
            styleElement.textContent.replace(regex, `:${replacement}`);
        }

        return styleElement;
    }

    /**
     * Builds the actual elements for the field
     * @param rootWrapper Primary wrapper for the fields
     */
    buildElements(rootWrapper) {
        if (this.noElements) {
            console.error(`Unable to find elements for form ${this.configuration?.id ?? "Unknown"}`);
            return;
        }

        this.elements = elementBuilder(this, rootWrapper);
    }

    /**
     * Creates the submit button and its corresponding handler
     * @param wrapper The host wrapper (div or form) to place the submit in
     */
    submitHandler(wrapper) {
        submitHandlerFactory(this, wrapper).render();
    }

    /**
     * Default handler for when a data element has changed - handles any logic required for the form
     * @param event The event that was passed to the listener
     */
    onChange(event) {
    }

    /**
     * Handles when the form is destroyed. Must be called to execute.
     */
    destroy() {
        if (this.resizeObserver) {
            this.resizeObserver.unobserve(this.shadowHost);
            this.resizeObserver.disconnect();

            this.resizeObserver = null;
        }

        if (this.subscriptions && Array.isArray(this.subscriptions)) {
            this.subscriptions.forEach(subscription => subscription.unsubscribe());
            this.subscriptions = [];
        }
    }

}
